import { ethers } from "ethers";
import { convertBNBToUSD, formatUSD } from "./priceAPI";

/**
 * Calculate the multiplier for payout calculation
 * This should match the backend's calculateMultiplier function
 * @param currentRow Current row number
 * @param rowConfigs Array of row configurations
 * @param skippedRowIdx Optional row index that was skipped (excludes from multiplier calculation)
 * @param phoenixRevivedOnRow Optional row index where Phoenix Revival was used (resets multiplier from that row)
 */
export function calculateMultiplier(currentRow: number, rowConfigs: any[], skippedRowIdx?: number, phoenixRevivedOnRow?: number): number {
  if (currentRow === 0) {
    return 1.0;
  }
  if (currentRow === 1 && skippedRowIdx === 0) {
    return 1.0;
  }

  let cumulativeMultiplier = 1.0;
  
  // Determine starting row for multiplier calculation
  // If Phoenix Revival was used, start calculating from the row after revival (reset multiplier)
  const startRow = phoenixRevivedOnRow !== undefined ? phoenixRevivedOnRow + 1 : 0;
  
  for (let i = startRow; i < currentRow; i++) {
    // Skip multiplier calculation for the row that was skipped
    if (skippedRowIdx !== undefined && i === skippedRowIdx) {
      continue;
    }

    const tilesInRow = rowConfigs[i].tiles;
    // Floor to 2 decimals for each row
    const baseMultiplier = Math.floor((tilesInRow / (tilesInRow - 1)) * 100) / 100;
    cumulativeMultiplier *= baseMultiplier;
  }

  // Apply 5% house edge and floor to 2 decimals
  return Math.floor(cumulativeMultiplier * 0.95 * 100) / 100;
}

/**
 * Calculate the potential payout amount
 * This should match the backend's calculatePayoutAmount function
 * @param betAmount Bet amount in ETH string format
 * @param currentRow Current row number
 * @param rowConfigs Array of row configurations
 * @param skippedRowIdx Optional row index that was skipped
 * @param phoenixRevivedOnRow Optional row index where Phoenix Revival was used
 */
export function calculatePotentialPayout(
  betAmount: string,
  currentRow: number,
  rowConfigs: any[],
  skippedRowIdx?: number,
  phoenixRevivedOnRow?: number
): string {
  if (currentRow === 0) {
    return betAmount;
  }

  const multiplier = calculateMultiplier(currentRow, rowConfigs, skippedRowIdx, phoenixRevivedOnRow);
  const bet = ethers.parseEther(betAmount);

  // Calculate payout amount
  const payoutAmount = (bet * BigInt(Math.floor(multiplier * 1000))) / BigInt(1000);

  return ethers.formatEther(payoutAmount);
}

/**
 * Format multiplier with at most 3 significant digits
 */
export function formatMultiplier(value: number): string {
  if (value >= 100) {
    // For values >= 100, show only whole numbers
    return Math.floor(value).toString() + "x";
  } else if (value >= 10) {
    // For values >= 10, show one decimal place
    return (Math.floor(value * 10) / 10).toFixed(1) + "x";
  } else {
    // For values < 10, show two decimal places
    return (Math.floor(value * 100) / 100).toFixed(2) + "x";
  }
}

/**
 * Format payout amount with appropriate precision based on value
 */
export function formatPayoutAmount(payout: number, currency: string = "BNB"): string {
  // Format payout with appropriate precision based on value
  if (payout >= 100) {
    return Math.floor(payout * 10) / 10 + ` ${currency}`; // One decimal place for large values
  } else if (payout >= 10) {
    return Math.floor(payout * 100) / 100 + ` ${currency}`; // Two decimal places for medium values
  } else if (payout >= 0.1) {
    return Math.floor(payout * 1000) / 1000 + ` ${currency}`; // Three decimal places for small values
  } else {
    return Math.floor(payout * 10000) / 10000 + ` ${currency}`; // Four decimal places for very small values
  }
}

/**
 * Format DXF balance for display with appropriate precision
 */
export function formatDXFBalance(balance: string): string {
  const num = parseFloat(balance);
  if (num === 0) return "0";
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(2)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(2)}K`;
  }
  if (num >= 1) {
    return num.toFixed(2);
  }
  return num.toFixed(4);
}

/**
 * Calculate potential payout in USD
 */
export function calculatePotentialPayoutUSD(
  betAmount: string,
  currentRow: number,
  rowConfigs: any[],
  bnbPrice: number,
  skippedRowIdx?: number,
  phoenixRevivedOnRow?: number
): number {
  const payoutBNB = parseFloat(calculatePotentialPayout(betAmount, currentRow, rowConfigs, skippedRowIdx, phoenixRevivedOnRow));
  return convertBNBToUSD(payoutBNB, bnbPrice);
}

/**
 * Format payout amount with USD equivalent
 */
export function formatPayoutWithUSD(payout: number, bnbPrice: number, currency: string = "BNB"): string {
  const bnbFormatted = formatPayoutAmount(payout, currency);
  const usdAmount = convertBNBToUSD(payout, bnbPrice);
  const usdFormatted = formatUSD(usdAmount);
  return `${bnbFormatted} (${usdFormatted})`;
}

/**
 * Calculate earnings for completing a specific row in USD
 */
export function calculateRowEarningsUSD(
  betAmount: string,
  fromRow: number,
  toRow: number,
  rowConfigs: any[],
  bnbPrice: number,
  skippedRowIdx?: number,
  phoenixRevivedOnRow?: number
): number {
  const fromPayout = calculatePotentialPayoutUSD(betAmount, fromRow, rowConfigs, bnbPrice, skippedRowIdx, phoenixRevivedOnRow);
  const toPayout = calculatePotentialPayoutUSD(betAmount, toRow, rowConfigs, bnbPrice, skippedRowIdx, phoenixRevivedOnRow);
  return toPayout - fromPayout;
}

/**
 * Get next row earnings preview in USD
 */
export function getNextRowEarningsUSD(
  betAmount: string,
  currentRow: number,
  rowConfigs: any[],
  bnbPrice: number,
  skippedRowIdx?: number,
  phoenixRevivedOnRow?: number
): number {
  if (currentRow >= rowConfigs.length) return 0;
  return calculateRowEarningsUSD(betAmount, currentRow, currentRow + 1, rowConfigs, bnbPrice, skippedRowIdx, phoenixRevivedOnRow);
}
